import React, { useState, useEffect } from 'react';
import { Upload, FileText, X, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { studentsAPI, staffAPI, consultantsAPI, masterSettingsAPI } from '@/api/api';
import { useNavigate } from 'react-router-dom';
import { readFileSmart } from '@/utils/imageCompressor';

const UploadCertificate = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ entity_id: '', entity_name: '', roll_no: '', title: '', course: '', file_name: '', file_data: '', file_type: '', file_size: 0, description: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, coursesRes] = await Promise.all([
        api.get('/students'),
        api.get('/master-settings/courses')
      ]);
      setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
      setCourses(Array.isArray(coursesRes.data) ? coursesRes.data : []);
    } catch (error) { console.error('Error:', error); }
  };

  const processSelectedFile = async (file, originalName, originalSize, mimeType) => {
    if (originalSize > 5 * 1024 * 1024) { alert('File size must be less than 5MB'); return; }
    try {
      const result = await readFileSmart(file);
      setFormData(prev => ({
        ...prev,
        file_name: originalName,
        file_data: result.data,
        file_type: result.type || (mimeType.split('/')[1] || 'pdf'),
        file_size: result.size
      }));
      if (result.compressed) {
        console.log(`Image compressed: ${(originalSize/1024).toFixed(0)}KB -> ${(result.size/1024).toFixed(0)}KB`);
      }
    } catch (err) {
      console.error('File read failed:', err);
      alert('Failed to read file. Please try again.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Snapshot primitives synchronously, then hand off to async processor
    processSelectedFile(file, file.name, file.size, file.type || 'application/octet-stream');
  };

  const handleStudentSelect = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (student) { setFormData({ ...formData, entity_id: student.id, entity_name: student.full_name, roll_no: student.roll_no || '', course: student.course || '' }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_data || !formData.entity_id) { alert('Please select student and file'); return; }
    setLoading(true);
    try {
      await api.post('/transcripts/documents', { ...formData, entity_type: 'student', document_type: 'certificate' });
      alert('Certificate uploaded!');
      navigate('/transcripts/certificate');
    } catch (error) { alert('Failed to upload'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/transcripts/certificate')}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
        <div><h1 className="text-2xl font-bold text-gray-900">Upload Certificate</h1><p className="text-sm text-gray-500 mt-1">Upload student certificate</p></div>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><Label>Select Student *</Label><Select value={formData.entity_id} onValueChange={handleStudentSelect}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{students.map(s => (<SelectItem key={s.id} value={s.id}>{s.full_name} - {s.roll_no}</SelectItem>))}</SelectContent></Select></div>
              <div><Label>Roll No</Label><Input value={formData.roll_no} readOnly className="bg-gray-50" /></div>
              <div><Label>Course</Label><Select value={formData.course} onValueChange={(v) => setFormData({...formData, course: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{courses.map(c => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent></Select></div>
              <div><Label>Certificate Title *</Label><Input required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Course Completion" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Optional" /></div>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} className="hidden" id="file-upload" />
              <label htmlFor="file-upload" className="cursor-pointer"><Upload className="h-12 w-12 mx-auto text-gray-400 mb-3" /><p className="text-gray-600 font-medium">Click to upload</p><p className="text-sm text-gray-500 mt-1">PDF, JPG, PNG (max 5MB)</p></label>
              {formData.file_name && (<div className="mt-4 flex items-center justify-center gap-2 text-green-600"><FileText className="h-5 w-5" /><span>{formData.file_name}</span><button type="button" onClick={() => setFormData({...formData, file_name: '', file_data: '', file_type: '', file_size: 0})}><X className="h-4 w-4 text-red-500" /></button></div>)}
            </div>
            <div className="flex gap-4"><Button type="button" variant="outline" onClick={() => navigate('/transcripts/certificate')} className="flex-1">Cancel</Button><Button type="submit" disabled={loading} className="flex-1">{loading ? 'Uploading...' : 'Upload'}</Button></div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadCertificate;